import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import type { RecipeSchemaProps } from './RecipeSchema.types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * RecipeSchema Component
 *
 * Security Note:
 * This component uses JSON.stringify() to output data into a <script> tag. While this is
 * generally safe because:
 * - JSON.stringify() automatically escapes dangerous characters
 * - The script uses type="application/ld+json" which is not executed as JavaScript
 *
 * IMPORTANT: The c_recipeSchema data MUST come from a trusted source (CMS/backend via BFF API)
 * and NOT from user input. Only pass recipe data that has been validated and sanitized by
 * the backend. Using untrusted user input here could lead to XSS vulnerabilities.
 *
 * @param recipe - Recipe object from BFF API (trusted backend source)
 * @returns JSX.Element | null - Structured data script tag or null if no schema available
 */
export const RecipeSchema = ({ recipe }: RecipeSchemaProps): JSX.Element | null => {
    const schemaObject = useMemo<Record<string, unknown> | null>(() => {
        if (!recipe) {
            return null;
        }

        const { c_recipeSchema } = recipe;

        if (!c_recipeSchema) {
            return null;
        }

        // Parse string values to avoid double-encoding which would cause the crawler to ignore the schema
        if (typeof c_recipeSchema === 'string') {
            const parsed: unknown = JSON.parse(c_recipeSchema);
            if (isRecord(parsed)) {
                return parsed;
            }
            return null;
        }

        if (isRecord(c_recipeSchema)) {
            return c_recipeSchema;
        }

        return null;
    }, [recipe]);

    if (!schemaObject) {
        return null;
    }

    return (
        <Helmet>
            <script type="application/ld+json" data-testid="recipe-schema">
                {JSON.stringify(schemaObject, null, 2)}
            </script>
        </Helmet>
    );
};
